import { QueryKey, queryOptions, useQuery, UseQueryOptions, useInfiniteQuery, UseInfiniteQueryOptions, InfiniteData } from '@tanstack/react-query'

import { HttpError } from '@/api/http'
import { getNextPageParamHelper } from '../utils'
import { getChargingStatus, getChargingHistory, getChargingConfig, getChargingHistoryStations } from './services'
import {
  GetChargingStatusPayload,
  GetChargingStatusResponse,
  GetChargingHistoryResponse,
  GetChargingConfigResponse,
  GetChargingHistoryStationsResponse,
  ChargingHistoryFilters,
} from './types'

export const createChargingHistoryQueryOptions = () =>
  queryOptions({
    queryKey: ['charging-history'],
    queryFn: () => getChargingHistory(),
  })

export const useQueryChargingConfig = (
  options?: Omit<UseQueryOptions<GetChargingConfigResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['charging-config']
  const queryFn = () => getChargingConfig()

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}

export const useQueryChargingStatus = (
  params: GetChargingStatusPayload,
  options?: Omit<UseQueryOptions<GetChargingStatusResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['charging-status', params]
  const queryFn = () => getChargingStatus(params)

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}

export const useQueryChargingHistory = (
  filters: ChargingHistoryFilters = {},
  options?: Omit<UseQueryOptions<GetChargingHistoryResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['charging-history', filters]
  const queryFn = () => getChargingHistory(filters)

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}

export const useInfiniteQueryChargingHistory = (
  filters: ChargingHistoryFilters = {},
  options?: Omit<UseInfiniteQueryOptions<GetChargingHistoryResponse, HttpError, InfiniteData<GetChargingHistoryResponse>, QueryKey, number>, 'queryKey' | 'queryFn'>
) => {
  return useInfiniteQuery({
    queryKey: ['charging-history-infinite', filters],
    queryFn: ({ pageParam = 1 }) => getChargingHistory({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: getNextPageParamHelper,
    ...options,
  })
}

export const useQueryChargingHistoryStations = (
  options?: Omit<UseQueryOptions<GetChargingHistoryStationsResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['charging-history-stations']
  const queryFn = () => getChargingHistoryStations()

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}
